import { useEffect, useRef } from 'react'

interface ShortcutConfig {
  key: string
  ctrl?: boolean
  meta?: boolean
  shift?: boolean
  alt?: boolean
  action: () => void
  description: string
  preventDefault?: boolean
}

export function useKeyboardShortcuts(shortcuts: ShortcutConfig[]) {
  const shortcutsRef = useRef(shortcuts)

  useEffect(() => {
    shortcutsRef.current = shortcuts
  }, [shortcuts])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorar si el usuario está escribiendo en un input/textarea
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Permitir Escape para cerrar dialogs incluso en inputs
        if (e.key !== 'Escape') return
      }

      for (const shortcut of shortcutsRef.current) {
        const match =
          e.key.toLowerCase() === shortcut.key.toLowerCase() &&
          !!e.ctrlKey === !!shortcut.ctrl &&
          !!e.metaKey === !!shortcut.meta &&
          !!e.shiftKey === !!shortcut.shift &&
          !!e.altKey === !!shortcut.alt

        if (match) {
          if (shortcut.preventDefault !== false) {
            e.preventDefault()
          }
          shortcut.action()
          break
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
}

// Shortcuts globales de la app
export function useGlobalKeyboardShortcuts(actions: {
  nuevoTurno?: () => void
  editarTurno?: () => void
  cerrarDialog?: () => void
  irAgenda?: () => void
  irClientes?: () => void
  irAjustes?: () => void
  toggleTema?: () => void
  abrirPaletaComandos?: () => void
}) {
  const shortcuts: ShortcutConfig[] = [
    // Navegación global
    {
      key: 'g',
      shift: true,
      action: () => actions.irAgenda?.(),
      description: 'Ir a Agenda (G + A)',
    },
    {
      key: 'c',
      shift: true,
      meta: false,
      ctrl: false,
      action: () => actions.irClientes?.(),
      description: 'Ir a Clientes (G + C)',
    },
    {
      key: 's',
      shift: true,
      meta: false,
      ctrl: false,
      action: () => actions.irAjustes?.(),
      description: 'Ir a Ajustes (G + S)',
    },

    // Acciones principales
    {
      key: 'n',
      action: () => actions.nuevoTurno?.(),
      description: 'Nuevo turno (N)',
    },
    {
      key: 'n',
      shift: true,
      action: () => actions.nuevoTurno?.(),
      description: 'Nuevo cliente (Shift+N)',
    },
    {
      key: 'e',
      action: () => actions.editarTurno?.(),
      description: 'Editar turno (E)',
    },
    {
      key: 'Escape',
      action: () => actions.cerrarDialog?.(),
      description: 'Cerrar dialog/Escape (Esc)',
      preventDefault: true,
    },

    // Utilidades
    {
      key: 't',
      action: () => actions.toggleTema?.(),
      description: 'Cambiar tema (T)',
    },
    {
      key: 'k',
      meta: true,
      action: () => actions.abrirPaletaComandos?.(),
      description: 'Paleta de comandos (⌘K)',
    },
    {
      key: 'k',
      ctrl: true,
      action: () => actions.abrirPaletaComandos?.(),
      description: 'Paleta de comandos (Ctrl+K)',
    },
  ]

  useKeyboardShortcuts(shortcuts)

  return shortcuts
}

// Hook específico para la Agenda
export function useAgendaKeyboardShortcuts(actions: {
  nuevoTurno: () => void
  editarTurno: (turnoId: string) => void
  eliminarTurno: (turnoId: string) => void
  navegarDia: (delta: number) => void
  navegarSemana: (delta: number) => void
  irAHoy: () => void
  cambiarVista: (vista: 'dia' | 'semana' | 'mes') => void
  seleccionarTurno: (turnoId: string | null) => void
  turnoSeleccionado: string | null
}) {
  const shortcuts: ShortcutConfig[] = [
    // Navegación de fechas
    {
      key: 'ArrowLeft',
      action: () => actions.navegarDia(-1),
      description: 'Día anterior (←)',
    },
    {
      key: 'ArrowRight',
      action: () => actions.navegarDia(1),
      description: 'Día siguiente (→)',
    },
    {
      key: 'ArrowUp',
      shift: true,
      action: () => actions.navegarSemana(-1),
      description: 'Semana anterior (Shift+↑)',
    },
    {
      key: 'ArrowDown',
      shift: true,
      action: () => actions.navegarSemana(1),
      description: 'Semana siguiente (Shift+↓)',
    },
    {
      key: 'h',
      action: () => actions.irAHoy(),
      description: 'Ir a hoy (H)',
    },

    // Cambiar vista
    {
      key: '1',
      action: () => actions.cambiarVista('dia'),
      description: 'Vista día (1)',
    },
    {
      key: '2',
      action: () => actions.cambiarVista('semana'),
      description: 'Vista semana (2)',
    },
    {
      key: '3',
      action: () => actions.cambiarVista('mes'),
      description: 'Vista mes (3)',
    },

    // Acciones sobre turno seleccionado
    {
      key: 'Enter',
      action: () => {
        if (actions.turnoSeleccionado) {
          actions.editarTurno(actions.turnoSeleccionado)
        } else {
          actions.nuevoTurno()
        }
      },
      description: 'Editar/Crear turno (Enter)',
    },
    {
      key: 'Delete',
      action: () => {
        if (actions.turnoSeleccionado) {
          if (confirm('¿Eliminar este turno?')) {
            actions.eliminarTurno(actions.turnoSeleccionado)
          }
        }
      },
      description: 'Eliminar turno (Del)',
    },
    {
      key: 'Escape',
      action: () => actions.seleccionarTurno(null),
      description: 'Deseleccionar turno (Esc)',
    },
  ]

  useKeyboardShortcuts(shortcuts)

  return shortcuts
}

// Hook para listas (Clientes, Empleados, Servicios)
export function useListKeyboardShortcuts(actions: {
  nuevo: () => void
  editar: (id: string) => void
  eliminar: (id: string) => void
  seleccionado: string | null
  buscar: () => void
}) {
  const shortcuts: ShortcutConfig[] = [
    {
      key: 'n',
      action: () => actions.nuevo(),
      description: 'Nuevo (N)',
    },
    {
      key: 'e',
      action: () => {
        if (actions.seleccionado) actions.editar(actions.seleccionado)
      },
      description: 'Editar seleccionado (E)',
    },
    {
      key: 'Delete',
      action: () => {
        if (actions.seleccionado && confirm('¿Eliminar?')) actions.eliminar(actions.seleccionado)
      },
      description: 'Eliminar seleccionado (Del)',
    },
    {
      key: '/',
      action: () => actions.buscar(),
      description: 'Buscar (/)',
    },
    {
      key: 'Escape',
      action: () => {},
      description: 'Limpiar selección (Esc)',
    },
  ]

  useKeyboardShortcuts(shortcuts)

  return shortcuts
}

// Helper para mostrar ayuda de atajos
export function useShortcutsHelp() {
  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === '?') {
        e.preventDefault()
        setShowHelp(true)
      }
      if (e.key === 'Escape' && showHelp) {
        setShowHelp(false)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [showHelp])

  return { showHelp, setShowHelp }
}

import { useState } from 'react'